import { useState, useEffect, useRef } from "react";
import Card from "../Card";
import FormField from "../FormField";
import SectionHeader from "../SectionHeader";
import DateSelect from "Components/Common/DateSelect/DateSelect";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";
import { formatDate } from "utils/Common/Date";
import Button from "Components/Common/Button/Button";

interface Props {
  prefill?: ProgressReportPrefill;
  invoiceAmount?: string;
  invoiceDate?: string;
  servicesRequested?: string;
  invoiceNumber?: string;
  recommendation?: string;
  recommendationText?: string;
  onRecommendationChange?: (id: string) => void;
  onRecommendationTextChange?: (text: string) => void;
  onSignatureDateChange?: (date: string) => void;
  signatureDateValue?: string;
  attachmentsList?: string;
  onAttachmentsListChange?: (val: string) => void;
  payableTo?: string;
  onPayableToChange?: (val: string) => void;
  mailTo?: string;
  onMailToChange?: (val: string) => void;
  submitted?: boolean;
}

interface RecommendationOption {
  id: string;
  label: string;
  icon: string;
  sub: string;
  tone: string;
  text: string;
}

const options: RecommendationOption[] = [
  {
    id: "approve",
    label: "Pay the Draw",
    icon: "✅",
    sub: "Recommend full payment upon lender approval",
    tone: "approve",
    text: "Pay the Draw as noted in this report upon Lender approval.",
  },
  {
    id: "hold",
    label: "Pay with Hold",
    icon: "⏸️",
    sub: "Approve draw less flagged line items pending re-inspection",
    tone: "hold",
    text: "Pay draw with hold on flagged line items pending re-inspection and work verification.",
  },
  {
    id: "reject",
    label: "Do Not Pay",
    icon: "🛑",
    sub: "Recommend rejection pending resolution of flags",
    tone: "reject",
    text: "Do not release funds at this time. Hold draw pending GC response and re-inspection.",
  },
];

const Step7InvoiceRecommendation = ({
  prefill,
  invoiceAmount = "",
  invoiceDate = "",
  servicesRequested = "",
  invoiceNumber = "",
  recommendation = "approve",
  recommendationText = "",
  onRecommendationChange,
  onRecommendationTextChange,
  onSignatureDateChange,
  signatureDateValue = "",
  attachmentsList = "",
  onAttachmentsListChange,
  payableTo = "",
  onPayableToChange,
  mailTo = "",
  onMailToChange,
  submitted = false,
}: Props) => {
  const [selected, setSelected] = useState<RecommendationOption>(options[0]);
  const [recText, setRecText] = useState(options[0].text);
  const [signatureDate, setSignatureDate] = useState(
    signatureDateValue || invoiceDate,
  );
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const found = options.find((o) => o.id === recommendation);
    if (found) setSelected(found);
  }, [recommendation]);

  useEffect(() => {
    if (recommendationText) setRecText(recommendationText);
  }, [recommendationText]);

  useEffect(() => {
    if (!signatureDateValue) {
      setSignatureDate(invoiceDate);
      onSignatureDateChange?.(invoiceDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate]);

  const recTextErr =
    submitted && !recText.trim() ? "Recommendation Text is required" : "";
  const sigDateErr =
    submitted && !signatureDate.trim() ? "Signature Date is required" : "";
  const mailToErr =
    mailTo.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailTo.trim())
      ? "Enter a valid email address"
      : "";

  useEffect(() => {
    if (submitted && (recTextErr || sigDateErr || mailToErr)) {
      rootRef.current
        ?.querySelector<HTMLElement>(".field-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const handleSignatureDateChange = (date: string) => {
    setSignatureDate(date);
    onSignatureDateChange?.(date);
  };

  const handleSelect = (option: RecommendationOption) => {
    setSelected(option);
    setRecText(option.text);
    onRecommendationChange?.(option.id);
    onRecommendationTextChange?.(option.text);
  };

  const handleRecTextChange = (val: string) => {
    setRecText(val);
    onRecommendationTextChange?.(val);
  };

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") ?? "{}");
    } catch {
      return {};
    }
  })();
  const reportBy = storedUser?.companyName ?? "";

  const stakeholders = prefill?.stakeholders;
  const project = prefill?.project;

  const lenderName = stakeholders?.lender?.companyName ?? "";
  const lendingOfficer = stakeholders?.lendingOfficer
    ? `${stakeholders.lendingOfficer.firstName ?? ""} ${stakeholders.lendingOfficer.lastName ?? ""}`.trim()
    : "";
  const toLine = [lenderName, lendingOfficer].filter(Boolean).join(" · ");

  const rows: [string, string][] = [
    ["To", toLine],
    ["Address", project?.address ?? ""],
    ["Date", formatDate(invoiceDate, false)],
    ["Project", project?.name ?? ""],
    ["For", servicesRequested || "—"],
  ];

  return (
    <div className="craScreen" ref={rootRef}>
      <SectionHeader
        title="CSC Invoice and Recommendation"
        subtitle="Set CSC fee invoice details and formal payment recommendation to the bank."
      />

      <Card
        title="CSC Invoice"
        badge={{ label: "Editable", variant: "editable" }}
      >
        <div className="invoicePreview">
          <div className="invoicePreview__head">
            <h4>INVOICE</h4>
            <span>
              {invoiceNumber
                ? `Invoice ${invoiceNumber.startsWith("#") ? invoiceNumber : `#${invoiceNumber}`}`
                : ""}
            </span>
          </div>
          <div className="invoicePreview__body">
            {rows.map(([label, val]) => (
              <div key={label} className="invoicePreview__row">
                <span>{label}</span>
                <strong>{val || "—"}</strong>
              </div>
            ))}
          </div>
          <div className="invoicePreview__total">
            <span>TOTAL DUE THIS INVOICE</span>
            <strong>{invoiceAmount ? `$${invoiceAmount}` : "-"}</strong>
          </div>
        </div>
        <div className="craFieldRow cols3 mt12">
          <FormField
            label="Invoice Amount"
            required
            value={invoiceAmount}
            type="number"
            icon="$"
            otherProps={{ min: 0, disabled: true }}
          />
          <FormField
            label="Make Check Payable To"
            value={payableTo}
            onChange={onPayableToChange}
          />
          <FormField
            label="Mail Check To"
            value={mailTo}
            onChange={onMailToChange}
            error={mailToErr}
          />
        </div>
      </Card>

      <Card title="Payment Recommendation">
        <div className="recommendationGrid">
          {options.map((option) => {
            const isSelected = selected.id === option.id;
            return (
              <Button
                key={option.id}
                type="button"
                className={`recOption ${option.tone} ${isSelected ? "selected" : ""}`}
                onClick={() => handleSelect(option)}
              >
                <p className="recOption__icon">{option.icon}</p>
                <strong>{option.label}</strong>
                <p>{option.sub}</p>
              </Button>
            );
          })}
        </div>
        <FormField
          label="Recommendation Text"
          required
          as="textarea"
          value={recText}
          onChange={handleRecTextChange}
          rows={4}
          error={recTextErr}
        />
        <div className="mt12">
          <FormField
            label="Attachments List (will appear in report footer)"
            value={attachmentsList}
            onChange={onAttachmentsListChange}
          />
        </div>
      </Card>

      <Card title="Report Signatory">
        <div className="craFieldRow cols2">
          <FormField
            label="Report By"
            required
            value={reportBy}
            otherProps={{ disabled: true }}
          />
          <div className="craField">
            <DateSelect
              label="Signature Date"
              required
              value={signatureDate}
              onChange={handleSignatureDateChange}
            />
            {sigDateErr && <p className="field-error">{sigDateErr}</p>}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step7InvoiceRecommendation;
